import { useEffect, useRef, CSSProperties } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Card, Typography, Badge, Dropdown, Tag } from "antd";
import {
  CloseOutlined,
  MinusOutlined,
  MoreOutlined,
  BellOutlined,
  BellFilled,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useChat } from "src/shared/hooks/useChat";
import {
  selectMessagesForRoom,
  selectIsLoadingMessages,
  selectMutedRooms,
  selectAllRooms,
} from "src/domain/app/redux/chat/chat-selectors";
import { useDispatch } from "react-redux";
import { toggleMuteRoom } from "src/domain/app/redux/chat/chat-slice";
import { RoomType } from "src/shared/services/chat/dto";
import MessageList from "src/pages/chat/components/MessageList";
import MessageInput from "src/pages/chat/components/MessageInput";
import "./styles.less";

const { Text } = Typography;

interface ChatWindowProps {
  roomId: string;
  isMinimized?: boolean;
  onClose?: () => void;
  onMinimize?: () => void;
  onBack?: () => void;
  style?: CSSProperties;
  className?: string;
  variant?: "popup" | "fullpage";
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  roomId,
  isMinimized = false,
  onClose,
  onMinimize,
  onBack,
  style,
  className = "",
  variant = "popup",
}) => {
  const { messages: intlMessages } = useIntl();
  const dispatch = useDispatch();
  const { joinRoom, leaveRoom } = useChat();
  const messages = useSelector(selectMessagesForRoom(roomId));
  const isLoadingMessages = useSelector(selectIsLoadingMessages);
  const mutedRooms = useSelector(selectMutedRooms);
  const allRooms = useSelector(selectAllRooms);
  const hasJoinedRef = useRef(false);

  const isMuted = mutedRooms.includes(roomId);

  // Find room info
  const room = allRooms.find((r) => r.id === roomId);

  // Get room title
  const getRoomTitle = () => {
    if (!room) return intlMessages["chat.title"].toString();
    return room.otherParticipantName || intlMessages["chat.title"].toString();
  };

  const roomTitle = getRoomTitle();

  const getRoomTypeLabel = (roomType: RoomType) => {
    switch (roomType) {
      case RoomType.CoordinatorDoctor:
        return intlMessages["chat.doctor"] as string;
      case RoomType.CoordinatorPlanner:
        return intlMessages["chat.planner"] as string;
      case RoomType.CoordinatorFabricator:
        return intlMessages["chat.fabricator"] as string;
      default:
        return "";
    }
  };

  const getRoomTypeColor = (roomType: RoomType) => {
    switch (roomType) {
      case RoomType.CoordinatorDoctor:
        return "blue";
      case RoomType.CoordinatorPlanner:
        return "green";
      case RoomType.CoordinatorFabricator:
        return "orange";
      default:
        return "default";
    }
  };

  useEffect(() => {
    if (!hasJoinedRef.current) {
      hasJoinedRef.current = true;
      joinRoom(roomId);
    }

    return () => {
      leaveRoom();
    };
  }, [roomId]);

  const handleToggleMute = () => {
    dispatch(toggleMuteRoom(roomId));
  };

  const handleClose = () => {
    leaveRoom();
    onClose?.();
  };

  const menuItems = [
    {
      key: "mute",
      label: isMuted
        ? (intlMessages["chat.unmute"] as string)
        : (intlMessages["chat.mute"] as string),
      icon: isMuted ? <BellOutlined /> : <BellFilled />,
      onClick: handleToggleMute,
    },
  ];

  const unreadCount = 0;

  const baseClassName = `chat-window ${className} ${variant === "popup" ? "chat-window-popup" : "chat-window-fullpage"} ${isMinimized ? "minimized" : ""}`;

  return (
    <Card className={baseClassName} style={style} bordered={false}>
      <div
        className="window-header"
        onClick={isMinimized && onMinimize ? onMinimize : undefined}
      >
        <div className="header-left">
          {onBack && !isMinimized && (
            <ArrowLeftOutlined
              className="back-button"
              onClick={(e) => {
                e.stopPropagation();
                onBack();
              }}
            />
          )}
          <div className="room-info">
            <div className="room-name-row">
              <Text strong className="room-name">
                {roomTitle}
              </Text>
            </div>
            {(room?.title || room?.subtitle) && (
              <Text className="room-subtitle" type="secondary">
                {room.roomType !== undefined && (
                  <>{getRoomTypeLabel(room.roomType)} • </>
                )}
                {room.title}
                {room.title && room.subtitle && " • "}
                {room.subtitle}
              </Text>
            )}
          </div>
          {unreadCount > 0 && !isMinimized && (
            <Badge count={unreadCount} className="unread-badge" />
          )}
        </div>
        <div className="header-actions">
          {!isMinimized && (
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <MoreOutlined className="action-icon" />
            </Dropdown>
          )}
          {onMinimize && (
            <MinusOutlined className="action-icon" onClick={onMinimize} />
          )}
          {onClose && (
            <CloseOutlined className="action-icon" onClick={handleClose} />
          )}
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="window-content">
            <MessageList messages={messages} roomId={roomId} />
          </div>

          <div className="window-footer">
            <MessageInput roomId={roomId} />
          </div>
        </>
      )}
    </Card>
  );
};

export default ChatWindow;
